import { UseMutateAsyncFunction } from '@tanstack/react-query'
import { Dispatch, ReactNode, SetStateAction } from 'react'
import { UseFormWatch } from 'react-hook-form'
import {
  BgmFactor,
  FastCutProductShortType,
  LinkDetailType,
  SubtitleType,
} from 'service/list'
import { MyPointType } from 'service/point'
import { Me } from 'service/user'
import { saveLocalStorage } from 'utils/common'
import { FormValues } from 'utils/type'

export const PRODUCT_NAME_PATTERN = /^[^<>\uD800-\uDFFF]*$/
export const translatePlanName = (planName: string) => {
  let tPlanName = ''
  switch (planName) {
    case '라이트':
      tPlanName = 'LIGHT'
      break
    case '프로':
      tPlanName = 'PRO'
      break
    case '프리미엄':
      tPlanName = 'PREMIUM'
      break
  }
  return tPlanName
}

export const createLongFormVideoFormData = async (
  data: FormValues,
  userInfo: Me,
  setRequest: Dispatch<SetStateAction<boolean>>,
  isAIGenerated: boolean,
  setErrorText: Dispatch<SetStateAction<ReactNode>>,
  scrollToProducts: () => void,
  setEssentialCheck: Dispatch<SetStateAction<boolean>>,
  isOpenLinkMethod: boolean,
  selectedLinkIds: number[],
  myPointMutateAsync: UseMutateAsyncFunction<MyPointType, Error, void, unknown>,
  setFailWithShortModal: Dispatch<SetStateAction<boolean>>,
  thumbNailDivRef: React.MutableRefObject<HTMLDivElement | null>,
  transitionDivRef: React.MutableRefObject<HTMLDivElement | null>,
  isRequest: boolean,
  watch: UseFormWatch<FormValues>
) => {
  try {
    setRequest((prev) => !prev)
    if (window.gtag) {
      // Event snippet for 영상생성요청클릭 conversion page

      window.gtag('event', 'conversion', {
        send_to: 'AW-677452397/OM8ECK_B3IwaEO20hMMC',
        value: 1.0,
        currency: 'KRW',
      })
    } else {
      console.error('gtag is not defined')
    }
    let {
      mainTitle,
      products,
      voiceId,
      userVoiceId,
      bgmId,
      transitionId,
      aiPromptTypeId,
      aiToneTypeId,
      thumbnailId,
      subtitleId,
      thumbnailInfo,
      introId,
      includePrice,
      includeAdd,
      outroId,
      stickerId,
      stickerLocation,
      subtitleColorId,
      subtitleFontId,
      subtitleSizeId,
      thumbnailHistoryId,
      thumbnailMethod,
    } = data

    const prevInfo = {
      voiceId,
      userVoiceId,
      bgmId,
      transitionId,
      aiPromptTypeId,
      aiToneTypeId,
      subtitleId,
      introId,
      includePrice,
      includeAdd,
      outroId,
      stickerId,
      stickerLocation,
      subtitleColorId,
      subtitleFontId,
      subtitleSizeId,
    }
    saveLocalStorage('videoEditorData', prevInfo)
    mainTitle = mainTitle.trim()

    const products_trim = []
    for (const product of products) {
      if (product.link && product.productName) {
        products_trim.push({
          link: product.link.trim(),
          userScript: product.userScript,
          productName: product.productName.trim(),
          files: product.selectedFiles,
          selectedVideos: product.selectedVideos,
          selectedThumbnails: product.selectedThumbnails,
          selectedDetails: product.selectedDetails,
          extensionCollectId: product.extensionCollectId,
          withShort: product.withShort,
          subtitleFlag: product.subtitleFlag,
          bgmFlag: product.bgmFlag,
          purchaseInfoFlag: product.purchaseInfoFlag,
          productTemplateId: product.productTemplateId,
          productTitle:
            product.productTemplateId === 49 && product.productTitle === ''
              ? product.productName
              : product.productTitle,
        })
      }
    }

    // 대본 ai방식으로 선택시 상품의 각 userScript가 비어있으면 안됌
    const hasEmptyUserScript = products_trim.some(
      (el) => el.userScript.trim() === ''
    )

    if (hasEmptyUserScript && !isAIGenerated) {
      // 에러 처리

      scrollToProducts()
      setErrorText(
        (
          <>
            <div className="w-full">
              <p className="font-bold text-center text-[18px]">{`각 상품의 대본은 모두 입력되어야 합니다.`}</p>
            </div>
          </>
        ) as ReactNode
      )

      setEssentialCheck(true)
      return
    }

    //ai 방식으로 상품을 선택할때 조건
    // 비디오 / 썸네일 / 상세페이지 중 한개만 있어도 가능
    // products_trim을 가지고 위에 조건을 검사
    let checkProductResource = { index: 0, bool: false }
    if (products_trim && !isOpenLinkMethod && selectedLinkIds.length > 0) {
      products_trim?.forEach((el, index) => {
        const { selectedDetails, selectedThumbnails, selectedVideos } = el
        if (selectedThumbnails?.length === 0) {
          checkProductResource = { index, bool: true }
          console.log(checkProductResource)
        }
      })
      if (checkProductResource.bool) {
        scrollToProducts()
        setErrorText(
          <>
            <div className="w-full">
              <p className="font-bold text-center">
                {`[상품${checkProductResource.index + 1}] 에서`}
                <br />{' '}
                <span className="font-normal">
                  대표이미지 최소 1개를 선택해주세요!
                </span>
              </p>
              <div className="bg-[#EEF1F7] rounded-[4px] flex justify-center py-2 my-4 w-[295px]">
                <p>
                  <span className="text-[32px]">⁇ 💁🏻 ⁇ </span> <br />
                  <span className="font-bold text-main">
                    상품 대표 이미지가 왜 필요하죠?
                  </span>
                  <br /> 인트로를 만들 때<br /> 각 상품의 대표 이미지를 사용하기
                  때문이에요
                </p>
              </div>
            </div>
          </>
        )

        setEssentialCheck(true)
        checkProductResource = { index: 0, bool: false }
        return
      }
    }
    const withShortLen = products.filter((el) => el.withShort).length
    if (withShortLen > 0) {
      // true일 경우 캐시가 충분한지 확인해야 함
      try {
        const myPointData = await myPointMutateAsync()

        const productLen = withShortLen
        const productShortCash = productLen * 300
        const currentMyCash = myPointData?.totalPoint ?? 0

        // 캐시가 부족한 경우 모달 표시
        if (currentMyCash < productShortCash) {
          setRequest((prev) => !prev)
          return setFailWithShortModal((prev) => !prev)
        }
      } catch (error) {
        return console.error('myPoint 설정에 오류가 생겼습니다.', error)
      }
    }
    if (
      thumbnailId === 0 ||
      (thumbnailHistoryId === 0 && thumbnailMethod === 'AI') ||
      (thumbnailInfo.title1 === '' &&
        thumbnailInfo.title2 === '' &&
        thumbnailInfo.title3 === '')
    ) {
      setErrorText('썸네일을 디자인을 선택하고 생성해주세요.')
      setRequest((prev) => !prev)
      if (thumbNailDivRef.current) {
        thumbNailDivRef.current.focus()
        thumbNailDivRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
      setEssentialCheck(true)
      return
    }

    if (Number(watch('bgmId')) === 0) {
      setErrorText('배경음악을 선택해주세요.')
      setRequest((prev) => !prev)
      if (thumbNailDivRef.current) {
        thumbNailDivRef.current.focus()
        thumbNailDivRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
      setEssentialCheck(true)
      return
    }
    if (Number(watch('transitionId')) === 0) {
      setErrorText('화면 전환 효과를 선택해주세요.')
      setRequest((prev) => !prev)
      if (transitionDivRef.current) {
        transitionDivRef.current.focus()
        transitionDivRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
      setEssentialCheck(true)
      return
    }

    if (Number(watch('outroId')) === 0) {
      setErrorText('영상 마지막 구독 & 좋아요 요청 화면을 선택해주세요.')
      setRequest((prev) => !prev)
      if (transitionDivRef.current) {
        transitionDivRef.current.focus()
        transitionDivRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
      setEssentialCheck(true)
      return
    }

    const videoInfo = {
      mainTitle,
      products: products_trim,
      voiceId,
      userVoiceId,
      bgmId,
      transitionId,
      aiPromptTypeId,
      aiToneTypeId,
      thumbnailId,
      subtitleId,
      thumbnailInfo,
      check: isRequest,
      introId,
      includePrice,
      includeAdd,
      outroId,
      stickerId,
      stickerLocation,
      subtitleColorId,
      thumbnailHistoryId,
      subtitleFontId,
      subtitleSizeId,
    }

    if (videoInfo.voiceId === 0 && videoInfo.userVoiceId !== 0) {
      delete (videoInfo as any)?.voiceId
    } else if (videoInfo.userVoiceId === 0 && videoInfo.voiceId !== 0) {
      delete (videoInfo as any)?.userVoiceId
    }

    const formData = new FormData()

    // Append the non-file data
    // JSON 객체를 FormData로 변환하여 추가하는 재귀 함수
    const appendFormData = (
      data: string | object | null,
      rootName = '',
      formData = new FormData()
    ) => {
      if (
        typeof data === 'object' &&
        data !== null &&
        !(data instanceof File)
      ) {
        Object.entries(data).forEach(([key, value]) => {
          const formKey = rootName ? `${rootName}[${key}]` : key
          if (
            typeof value === 'object' &&
            value !== null &&
            !(value instanceof File)
          ) {
            appendFormData(value, formKey, formData) // 재귀적으로 객체 추가
          } else {
            formData.append(formKey, value)
          }
        })
      } else {
        // @ts-ignore
        formData.append(rootName, data)
      }
      return formData
    }

    // 객체 키들을 FormData에 추가
    Object.keys(videoInfo).forEach((key) => {
      if (key !== 'products') {
        // JSON 객체인 경우 재귀적으로 추가, 그렇지 않으면 그대로 추가
        // @ts-ignore
        if (typeof videoInfo[key] === 'object' && videoInfo[key] !== null) {
          // @ts-ignore
          appendFormData(videoInfo[key], key, formData)
        } else {
          // @ts-ignore
          formData.append(key, videoInfo[key])
        }
      }
    })

    // Append the product URLs and files
    videoInfo.products.forEach((product, productIndex) => {
      formData.append(`products[${productIndex}][link]`, product.link)
      if (!isOpenLinkMethod && selectedLinkIds.length > 0) {
        if (!product?.extensionCollectId || product?.extensionCollectId === 0) {
          console.log('error')
          throw new Error('extensionCollectId is Undefined')
        } else {
          formData.append(
            `products[${productIndex}][extensionCollectId]`,
            String(product.extensionCollectId)
          )
        }
      }
      formData.append(
        `products[${productIndex}][withShort]`,
        String(product.withShort)
      )
      formData.append(
        `products[${productIndex}][subtitleFlag]`,
        String(product.subtitleFlag)
      )
      formData.append(
        `products[${productIndex}][bgmFlag]`,
        String(product.bgmFlag)
      )

      formData.append(
        `products[${productIndex}][purchaseInfoFlag]`,
        String(product.purchaseInfoFlag)
      )

      formData.append(
        `products[${productIndex}][productName]`,
        product.productName
      )

      formData.append(
        `products[${productIndex}][userScript]`,
        product.userScript
      )
      formData.append(
        `products[${productIndex}][productTemplateId]`,
        String(product.productTemplateId)
      )
      formData.append(
        `products[${productIndex}][productTitle]`,
        product.productTitle
      )
      // Append the files for each product
      product?.files?.forEach((file: string | Blob, fileIndex: any) => {
        // 실제 파일 객체로 변환되어야 함
        if (file instanceof File) {
          formData.append(
            `products[${productIndex}][files][${fileIndex}]`,
            file
          )
        }
      })

      product?.selectedDetails?.forEach((url: string, fileIndex: any) => {
        // 실제 파일 객체로 변환되어야 함
        if (url && !isOpenLinkMethod) {
          formData.append(
            `products[${productIndex}][selectedDetails][${fileIndex}]`,
            url
          )
        }
      })
      product?.selectedThumbnails?.forEach((url: string, fileIndex: any) => {
        // 실제 파일 객체로 변환되어야 함
        if (url && !isOpenLinkMethod) {
          formData.append(
            `products[${productIndex}][selectedThumbnails][${fileIndex}]`,
            url
          )
        }
      })
      product?.selectedVideos?.forEach((url: string, fileIndex: any) => {
        // 실제 파일 객체로 변환되어야 함
        if (url && !isOpenLinkMethod) {
          formData.append(
            `products[${productIndex}][selectedVideos][${fileIndex}]`,
            url
          )
        }
      })
    })

    return formData
  } catch (e: any) {}
}

export const createShortFormVideoData = async (
  data: FormValues,
  subtitleList: SubtitleType[] | undefined,
  bgmList: BgmFactor[] | undefined
) => {
  // 1.localstorage FAST_CUT_PRODUCT_SHORT_DATA로 저장하자 FASTCUTPRODUCTSHORTSTYPE
  //"subtitleFlag":"1" 설정한 데이터가 없음인지 확인해서 얼라인
  // "bgmFlag":"1", > 설정한 데이터가 없은지 확인해서 얼라인
  const subtitle = subtitleList?.find((el) => el.id === data.subtitleId)
  const bgm = bgmList?.find((el) => el.id === data.bgmId)

  const product = data.products[0]
  const arrangeData: FastCutProductShortType = {
    product: {
      link: product.link,
      productName: product.productName,
      extensionCollectId: product.extensionCollectId,
      userScript: product.userScript,
      selectedVideos: product.selectedVideos,
      selectedThumbnails: product.selectedThumbnails,
      selectedDetails: product.selectedDetails,
      withShort: 1,
      subtitleFlag: subtitle?.name === '자막 없음' ? 0 : 1,
      bgmFlag: bgm?.name === '없음' ? 0 : 1,
      purchaseInfoFlag: product.purchaseInfoFlag,
      productTemplateId: product.productTemplateId,
      productTitle: product.productTitle,
    },
    aiToneTypeId: data.aiToneTypeId,
    aiPromptTypeId: data.aiPromptTypeId,
    voiceId: data.voiceId,
    userVoiceId: data.userVoiceId,
    bgmId: data.bgmId,
    subtitleId: data.subtitleId,
    subtitleColorId: data.subtitleColorId,
    subtitleFontId: data.subtitleFontId,
    subtitleSizeId: data.subtitleSizeId,
    includePrice: data.includePrice,
  }

  const fastCutProductShortPrevInfo = {
    aiToneTypeId: data.aiToneTypeId,
    aiPromptTypeId: data.aiPromptTypeId,
    voiceId: data.voiceId,
    userVoiceId: data.userVoiceId,
    bgmId: data.bgmId,
    subtitleId: data.subtitleId,
    subtitleColorId: data.subtitleColorId,
    subtitleFontId: data.subtitleFontId,
    subtitleSizeId: data.subtitleSizeId,
    includePrice: data.includePrice,
  }

  if (
    arrangeData.product.productTemplateId === 49 &&
    arrangeData.product.productTitle === ''
  ) {
    arrangeData.product.productTitle = arrangeData.product.productName
  }

  saveLocalStorage('productShortData', fastCutProductShortPrevInfo)

  return arrangeData

  // 2.위에 형태로 데이터가 정리되고 나면 내뱉으면
}
